import React, { PropsWithChildren, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { useFileRecordApi } from "../../../../common/api/master-api/file-records/useFileRecordApi";
import { FileRecordStatus } from "../../../../common/types/FileRecordStatus";
import { useAccessVerifier } from "../../../../core/security/useAccessVerifier";
import { FileRecordDetails, mapToFileRecordDetails } from "../../../common/file-record-details/FileRecordDetails";
import { FileRecordStatusDetails } from "../../common/types/FileRecordStatusDetails";
import { FileRecordDetailsStateContext } from "./FileRecordDetailsStateContext";

type FileRecordDetailsStateProviderProps = PropsWithChildren<unknown>;

export const FileRecordDetailsStateProvider = ({ children }: FileRecordDetailsStateProviderProps): JSX.Element => {
    const { fileRecordId } = useParams<{ fileRecordId: string }>();

    const [fileRecordDetails, setFileRecordDetails] = useState<FileRecordDetails>({} as FileRecordDetails);
    const [isArchivedContentAssigned, setIsArchivedContentAssigned] = useState<boolean>(false);

    const { isAuthorized } = useAccessVerifier();

    const [isLoading, setIsLoading] = useState<boolean>(true);

    const { getFileRecordDetails } = useFileRecordApi();

    useEffect(() => {
        if (!fileRecordId) {
            return;
        }

        callForData(fileRecordId);
    }, [fileRecordId]);

    const callForData = async (id: string): Promise<void> => {
        setIsLoading(true);

        getFileRecordDetails({ fileRecordId: id })
            .then((response) => {
                const details = mapToFileRecordDetails(response);

                setFileRecordDetails(details);
            })
            .finally(() => setIsLoading(false));
    };

    const setStatus = (status: FileRecordStatus, statusDetails?: FileRecordStatusDetails) => {
        setFileRecordDetails({
            ...fileRecordDetails,
            status,
            statusDetails
        });
    };

    return (
        <FileRecordDetailsStateContext.Provider
            value={{
                fileRecordDetails,
                setFileRecordDetails,
                isLoading,
                isAuthorized,
                setStatus,
                isArchivedContentAssigned,
                setIsArchivedContentAssigned
            }}
        >
            {children}
        </FileRecordDetailsStateContext.Provider>
    );
};
